<template>
  <component
    :is="icon"
    :style="iconStyles"
  />
</template>

<script lang="ts" setup>
  import { computed, defineAsyncComponent } from 'vue';

  export interface Props {
    /**
     * Icon name.
     *
     * This is an icon filename without extension.
     */
    name: string;

    /**
     * Icon filling color (css variable only).
     *
     * Set to an empty string to prevent the filling of the icon shape.
     */
    color?: string;

    /**
     * Icon stroke color (css variable only).
     */
    strokeColor?: string;

    /**
     * Icon size.
     *
     * Use a number value to set the icon size in pixels, and a string value for custom units.
     */
    size?: number | string;

    /**
     * Rotation degree.
     *
     * Use a number value to set the the rotation in degrees, and a string value for custom units.
     */
    rotate?: number | string;

    /**
     * Rotation animation speed.
     *
     * Use a number value to set the animation speed in seconds, and a string value for custom units.
     */
    animationSpeed?: number | string;
  }

  const props = withDefaults(defineProps<Props>(), {
    color: '--alias-color-icon-default',
    animationSpeed: '0.2s',
  });

  const iconStyles = computed(() => {
    const { rotate, size, animationSpeed, strokeColor, color } = props;
    const rotation = typeof rotate === 'number' ? `${rotate}deg` : rotate;
    const sizeStyle = typeof size === 'number' ? `${size}px` : size;
    const speed = typeof animationSpeed === 'number' ? `${animationSpeed}s` : animationSpeed;

    return {
      fill: color ? `var(${color})` : undefined,
      stroke: strokeColor ? `var(${strokeColor})` : undefined,
      transform: rotation ? `rotate(${rotation})` : undefined,
      width: sizeStyle,
      height: sizeStyle,
      transition: `all ${speed}`,
    };
  });

  const icon = defineAsyncComponent(() => import(`./svg/${props.name}.svg`));
</script>
